import { api } from '../api';
import { useInfiniteQuery, UseInfiniteQueryResult, useQuery, UseQueryResult } from 'react-query';
import { Entity } from '../types/entity';
import { entityHexColourCache } from './cache';
import { isMock } from '../utilities/is-mock';

export type GetEntitiesResponse = {
    entities: Entity[];
    totalItems: number;
};

type QueryParams = {
    page: number;
    limit?: number;
    search: string;
    sort: string;
    filter: string;
    searchFilters?: string;
};

export function generateAvatarColour(data: GetEntitiesResponse): GetEntitiesResponse {
    function genColour(id: string): string {
        if (entityHexColourCache[id]) {
            return entityHexColourCache[id];
        }
        const colours = ['#3b82f6', '#22c55e', '#ef4444', '#eab308', '#a855f7'];
        const profileHexColour = colours[Math.floor(Math.random() * colours.length)];
        entityHexColourCache[id] = profileHexColour;

        return profileHexColour;
    }

    return {
        ...data,
        entities: data.entities.map((e) => {
            return { ...e, avatarHexColour: genColour(e.id) };
        }),
    };
}

async function getEntities({ page, limit, search, sort, filter, searchFilters }: QueryParams): Promise<GetEntitiesResponse> {
    const query = isMock()
        ? `filter=${filter}&page=${page}&sort=${sort}&search=${search}${limit ? `&limit=${limit}` : ''}`
        : `${filter}&page=${page}&${sort}&search=${encodeURIComponent(search)}${limit ? `&limit=${limit}` : ''}&${searchFilters}`;

    const { data } = await api.get(`/entities?${query}`);

    return data;
}

export function useEntities({
    page,
    limit,
    search,
    sort,
    filter,
    searchFilters,
}: QueryParams): UseQueryResult<GetEntitiesResponse, unknown> {
    return useQuery(
        ['entities', page, limit, search, sort, filter, searchFilters],
        () => getEntities({ page, limit, search, sort, filter, searchFilters }),
        {
            keepPreviousData: true,
            select: generateAvatarColour,
        }
    );
}

export function useAllEntities(params: QueryParams, enabled = true): UseInfiniteQueryResult<GetEntitiesResponse, unknown> {
    return useInfiniteQuery(
        ['entities', ...Object.values(params)],
        ({ pageParam = 1 }) => getEntities({ ...params, page: pageParam, limit: params.limit || 500 }),
        {
            keepPreviousData: true,
            cacheTime: 0,
            enabled,
            getNextPageParam: (lastPage, allPages) => {
                if (lastPage.entities.length === (params.limit || 500)) {
                    return allPages.length + 1;
                }
                return undefined;
            },
        }
    );
}
